import {
  BrandJsonLdProps,
  EventJsonLdProps,
  NewsArticleJsonLdProps,
  OrganizationJsonLdProps,
  ProductJsonLdProps,
  SiteLinksSearchBoxJsonLdProps,
  SocialProfileJsonLdProps,
  WebPageJsonLdProps,
} from 'next-seo';
import DOMPurify from 'isomorphic-dompurify';
import removeMd from 'remove-markdown-and-html';
import { defaultInternationalization, defaultLanguage } from 'corporate-types';
import {
  capitalizeFirstLetter,
  internationalizationToLanguages,
  multipleToSingleSpace,
  removeMarkdownCustomText,
  stringToISODate,
} from 'corporate-utils';

import {
  CategoryJsonLd,
  Product,
  SingleStrapiImage,
} from '../models/domain/interfaces';
import { getStrapiMedia } from './media';
import {
  CompanyData,
  PageBreadcrumb,
  SocialCompanyData,
} from '../models/interface';
import { getTranslations } from './localize';
import { Workshop } from '../models/auto-generated-strapi-types/types/Workshop';
import { Newsa } from '../models/auto-generated-strapi-types/types/Newsa';
import { Event } from '../models/auto-generated-strapi-types/types/Event';
import { Logger } from 'corporate-utils';

export const mapToProductJsonLd = (
  product: Product,
  locale: string
): ProductJsonLdProps => {
  let description = '';

  try {
    description = DOMPurify.sanitize(
      product.descriptions
        ?.filter(
          (desc) =>
            desc?.language?.toLowerCase() ===
            (internationalizationToLanguages(
              locale || defaultInternationalization,
              'packages/corporate-ui/src/utils/schemas.ts A'
            ) || defaultLanguage)
        )[0]
        ?.description?.replace(/\\n/g, ' ')
        .replace(/\\r/g, '')
        .replace(/\n/g, ' ')
        .replace(/\r/g, '')
        .replace(/-/g, '') ?? ''
    );
    description = JSON.stringify(description?.trim() || '')
      ?.substring?.(1)
      ?.slice?.(0, -1);
  } catch (error) {
    Logger.error(error);
  }

  return {
    productName: multipleToSingleSpace(product.nameFather ?? ''),
    images: product.images?.map((image) =>
      getStrapiMedia(image).replace(/ /g, '%20')
    ),
    description: description || '',
    sku: product.sku,
    brand: product.brand ?? process.env.NEXT_PUBLIC_COMPANY_NAME ?? '',
  };
};

export const mapToNewsArticleJsonLd = (
  data: Newsa | Workshop,
  breadCrumb: PageBreadcrumb,
  metadata: any,
  translations: any,
  locale?: string
): NewsArticleJsonLdProps => {
  const url = `${process.env.NEXT_PUBLIC_URL}${
    breadCrumb.links[breadCrumb.links.length - 1].href
  }/`;
  const images: string[] = [];

  if (data.attributes.coverImage.data?.attributes?.url) {
    const url = data.attributes.coverImage.data?.attributes?.url;

    if (
      typeof url.startsWith === 'function' &&
      (url.startsWith('http') || url.startsWith('//'))
    ) {
      images.push(`${getStrapiMedia(url)}`);
    } else {
      images.push(`${getStrapiMedia(url)}`);
    }
  }

  if (
    (data as Newsa).attributes.bottomImages &&
    (data as Newsa).attributes.bottomImages?.data
  )
    (data as Newsa).attributes.bottomImages?.data
      ?.map((image) => {
        if (image.attributes?.url) {
          const url = image.attributes?.url;

          if (
            typeof url.startsWith === 'function' &&
            (url.startsWith('http') || url.startsWith('//'))
          ) {
            return `${getStrapiMedia(url)}`;
          } else {
            return `${process.env.NEXT_PUBLIC_URL}${getStrapiMedia(
              image.attributes.url
            )}`;
          }
        }
      })
      .forEach((imageUrl) => {
        return imageUrl && images.push(imageUrl);
      });

  const publisherLogo = metadata?.logo?.data?.attributes?.url;
  let publisherLogoUrl = '';

  if (
    typeof publisherLogo.startsWith === 'function' &&
    (publisherLogo.startsWith('http') || publisherLogo.startsWith('//'))
  ) {
    publisherLogoUrl = `${getStrapiMedia(publisherLogo)}`;
  } else {
    publisherLogoUrl = `${process.env.NEXT_PUBLIC_URL}${getStrapiMedia(
      publisherLogo
    )}`;
  }

  return {
    title: removeMd(data?.attributes?.title ?? ''),
    url,
    images,
    description: removeMd(
      removeMarkdownCustomText(data?.attributes?.description ?? '')
    ),
    section: capitalizeFirstLetter(
      data?.attributes?.category?.toLowerCase() ?? ''
    ),
    keywords: metadata?.keywords,
    dateCreated: stringToISODate(
      (data?.attributes as any)?.date ?? '',
      translations?.date?.singleNews?.format,
      locale
    ) as string,
    datePublished: stringToISODate(
      (data?.attributes as any)?.date ?? '',
      translations?.date?.singleNews?.format,
      locale
    ) as string,
    authorName: process.env.NEXT_PUBLIC_COMPANY_NAME ?? '',
    publisherName: process.env.NEXT_PUBLIC_COMPANY_NAME ?? '',
    publisherLogo: publisherLogoUrl,
    body: removeMd(removeMarkdownCustomText(data?.attributes?.text ?? '')),
  };
};

export const mapToCorporationJsonLd = (
  companyData: CompanyData
): OrganizationJsonLdProps => {
  return {
    organizationType: 'Corporation',
    logo: getStrapiMedia(companyData.logo?.data?.attributes?.url || ''),
    name: companyData?.companyTitle ?? '',
    address: {
      addressRegion: companyData.locationSchema?.addressRegion ?? '',
      streetAddress: companyData.locationSchema?.streetAddress ?? '',
      addressLocality: companyData.locationSchema?.addressLocality ?? '',
      postalCode: companyData.locationSchema?.postalCode ?? '',
      addressCountry: companyData.locationSchema?.addressCountry ?? '',
    },
    contactPoints: [
      {
        telephone: companyData?.companyPhoneValue ?? '',
        contactType: companyData?.companyPhoneTitle ?? '',
      },
      {
        telephone: companyData?.companyFaxValue ?? '',
        contactType: companyData?.companyFaxTitle ?? '',
      },
    ],
    url: process.env.NEXT_PUBLIC_COMPANY_SITE_URL ?? '',
  };
};

export const mapToEventJsonLd = (
  event: Event,
  translations: any,
  locale?: string
): EventJsonLdProps => {
  const images: string[] = [];

  if (event.attributes.coverImage.data?.attributes?.url) {
    const url = event.attributes.coverImage.data?.attributes?.url;

    if (
      typeof url.startsWith === 'function' &&
      (url.startsWith('http') || url.startsWith('//'))
    ) {
      images.push(
        `${getStrapiMedia(event.attributes.coverImage.data?.attributes?.url)}`
      );
    } else {
      images.push(
        `${getStrapiMedia(event.attributes.coverImage.data?.attributes?.url)}`
      );
    }
  }

  return {
    name: removeMd(event?.attributes?.title ?? ''),
    startDate: stringToISODate(
      (event?.attributes?.startDate as any) ?? '',
      translations.date.singleEvent.format,
      locale
    ) as string,
    endDate: stringToISODate(
      (event?.attributes?.endDate as any) ?? '',
      translations.date.singleEvent.format,
      locale
    ) as string,
    location: {
      name: `${event?.attributes?.title} ${translations?.schemas?.location?.address}`,
      address: {
        streetAddress: event?.attributes?.locationSchema?.streetAddress ?? '',
        addressLocality:
          event?.attributes?.locationSchema?.addressLocality ?? '',
        postalCode: event?.attributes?.locationSchema?.postalCode ?? '',
        addressCountry: event?.attributes?.locationSchema?.addressCountry ?? '',
        addressRegion: event?.attributes?.locationSchema?.addressRegion ?? '',
      },
    },
    url: event?.attributes?.website,
    images,
    description: removeMd(
      removeMarkdownCustomText(event?.attributes?.text || '')
    ),
  };
};

export const mapToSocialProfileJsonLd = (
  socialData: SocialCompanyData
): SocialProfileJsonLdProps => {
  return {
    type: 'Organization',
    name: socialData?.companyTitle ?? '',
    url: process.env.NEXT_PUBLIC_COMPANY_SITE_URL ?? '',
    sameAs: socialData?.socialLinks ?? '',
    logo: getStrapiMedia(socialData?.logo?.data?.attributes?.url || ''),
  } as SocialProfileJsonLdProps;
};

export const mapToSiteLinksSearchBoxJsonLd =
  (): SiteLinksSearchBoxJsonLdProps => {
    return {
      url: process.env.NEXT_PUBLIC_URL ?? '',
      potentialActions: [
        {
          target: `${process.env.NEXT_PUBLIC_URL}/search-product?q`,
          queryInput: 'searched_product',
        },
      ],
    };
  };

export const mapToWebPageJsonLd = (
  description: string,
  translations: any
): WebPageJsonLdProps => {
  return {
    id: `${process.env.NEXT_PUBLIC_URL}/${translations?.urls?.company}`,
    description,
  };
};

export const mapToBrandJsonLd = async (
  logo: SingleStrapiImage,
  locale = defaultInternationalization
): Promise<BrandJsonLdProps> => {
  const translations = await getTranslations(
    internationalizationToLanguages(
      locale || defaultInternationalization,
      'packages/corporate-ui/src/utils/schemas.ts B'
    ) || defaultLanguage
  );

  const logoL = logo?.data?.attributes?.url || '';
  let logoUrl = '';

  if (
    typeof logoL.startsWith === 'function' &&
    (logoL.startsWith('http') || logoL.startsWith('//'))
  ) {
    logoUrl = `${getStrapiMedia(logoL)}`;
  } else {
    logoUrl = `${process.env.NEXT_PUBLIC_URL}${getStrapiMedia(logoL)}`;
  }

  return {
    id: `${process.env.NEXT_PUBLIC_URL}/${translations?.urls?.company}`,
    logo: logoUrl,
  };
};

export const mapCategoryToProductJsonLd = (
  category: CategoryJsonLd
): ProductJsonLdProps => {
  let description = '';

  try {
    description = category?.description
      ? DOMPurify.sanitize(category?.description)
      : '';
  } catch (error) {
    Logger.error(error);
  }

  return {
    productName: multipleToSingleSpace(category?.title ?? ''),
    images:
      category?.image || category?.mobileImage
        ? [
          getStrapiMedia(category?.image || category?.mobileImage).replace(
            / /g,
            '%20'
          ),
        ]
        : [],
    description: description || '',
    brand: process.env.NEXT_PUBLIC_COMPANY_NAME ?? '',
  };
};
